/**
 * The Firebase claim flow — existing SYQEL subscribers (Desktop / VJ) prove email ownership
 * via passwordless email-link sign-in on syqel.art's DEDICATED Firebase project ('syqel-art':
 * its own 'SYQEL Art' email branding + the default action handler, zero contact with the
 * shared syqel-visualizer project that VJ/Desktop run on). /api/claim verifies the ID token
 * and grants entitlement from the verified email's Stripe record — which is why claims work
 * for subscribers of ANY SYQEL product regardless of which project authenticates them.
 * Uses Firebase's REST endpoints directly (public web API key) — no SDK in the bundle.
 */
import { desktopTokenLink } from './catalogue'

const API_KEY = 'AIzaSyDYlWsbic9aVWkrsyRKVgd-nkaGgqxbe_0' // public web config (syqel-art)
const BASE = 'https://identitytoolkit.googleapis.com/v1'

/**
 * Ask Firebase to email the sign-in link. `desktop` marks a claim started from the desktop app:
 * the flag rides continueUrl → /auth/action → the player, which then hands the entitlement token
 * back to the app over the syqel-art:// deep link (the app can't share the browser's cookie).
 */
export async function requestClaimLink(email: string, desktop = false): Promise<boolean> {
  try {
    localStorage.setItem('syqel.claimEmail', email)
    const res = await fetch(`${BASE}/accounts:sendOobCode?key=${API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        requestType: 'EMAIL_SIGNIN',
        email,
        // carry the email through the link for desktop: it opens in the system browser, which doesn't
        // share the app webview's localStorage, so completeClaimIfPresent can read it back seamlessly.
        continueUrl: desktop ? `https://syqel.art/?desktop=1&claimEmail=${encodeURIComponent(email)}` : 'https://syqel.art/',
        canHandleCodeInApp: true
      })
    })
    return res.ok
  } catch {
    return false
  }
}

export type ClaimResult = 'none' | 'claimed' | 'no-customer' | 'no-qualifying-sub' | 'fb-unconfigured' | 'fb-lookup-failed' | 'error'

/** If the page was opened from the sign-in link, finish the sign-in and claim entitlement. */
export async function completeClaimIfPresent(): Promise<ClaimResult> {
  const params = new URLSearchParams(window.location.search)
  const oobCode = params.get('oobCode')
  // capture the desktop flag BEFORE we strip the query below
  const desktop = params.get('desktop') === '1'
  if (params.get('mode') !== 'signIn' || !oobCode) return 'none'
  // A DESKTOP claim opens this link in the system browser, not the app's webview — so the email saved
  // by requestClaimLink lives in the app's localStorage, NOT here. Recover it from the link (newer app
  // builds pass it through `claimEmail`), then this browser's localStorage, then ask the user.
  let email = (params.get('claimEmail') ?? localStorage.getItem('syqel.claimEmail') ?? '').trim()
  history.replaceState(null, '', window.location.pathname + window.location.hash)
  if (!email) {
    email = (window.prompt('Confirm the email you subscribed with to finish claiming SYQEL Art') ?? '').trim()
  }
  if (!email) {
    console.warn('[syqel] claim: no email available to complete sign-in')
    return 'error'
  }
  try {
    const si = await fetch(`${BASE}/accounts:signInWithEmailLink?key=${API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, oobCode })
    })
    if (!si.ok) {
      console.warn('[syqel] claim sign-in failed:', si.status, await si.text().catch(() => ''))
      return 'error'
    }
    const { idToken } = (await si.json()) as { idToken?: string }
    if (!idToken) return 'error'
    const cl = await fetch('/api/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ idToken })
    })
    if (!cl.ok) {
      console.warn('[syqel] claim API failed:', cl.status, await cl.text().catch(() => ''))
      return 'error'
    }
    const data = (await cl.json()) as { subscribed?: boolean; reason?: string; message?: string; token?: string }
    if (data.reason === 'exception') console.warn('[syqel] claim worker exception:', data.message)
    if (data.subscribed) {
      // a desktop-initiated claim runs here in the BROWSER — pass the entitlement token to the
      // desktop app via its deep link (the app stores it and validates against /api/status)
      if (desktop && data.token) window.location.href = desktopTokenLink(data.token)
      return 'claimed'
    }
    const known = ['no-customer', 'no-qualifying-sub', 'fb-unconfigured', 'fb-lookup-failed']
    return (known.includes(data.reason ?? '') ? data.reason : 'no-qualifying-sub') as ClaimResult
  } catch {
    return 'error'
  }
}
